import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import { useAssignments } from '../../hooks/useAssignments';
import { useExams } from '../../hooks/useExams';
import { useGrades } from '../../hooks/useGrades';
import { academicService } from '../../services/academicService';
import { 
  BookOpen, 
  Calendar, 
  FileText, 
  Award,
  Clock,
  CheckCircle,
  AlertCircle
} from 'lucide-react';

const StudentDashboard = ({ user }) => {
  const { assignments, loading: assignmentsLoading } = useAssignments({ scope: 'student', limit: 4 });
  const { exams, loading: examsLoading } = useExams({ scope: 'student', timeframe: 'upcoming', limit: 3 });
  const { grades, loading: gradesLoading } = useGrades({ limit: 4 });
  const [timetable, setTimetable] = React.useState([]);
  const [announcements, setAnnouncements] = React.useState([]);
  const [ttLoading, setTtLoading] = React.useState(true);
  const [announcementsLoading, setAnnouncementsLoading] = React.useState(true);

  React.useEffect(() => {
    const run = async () => {
      try {
        setTtLoading(true);
        const tt = await academicService.getMyTimetable({ today: true });
        setTimetable(tt.data?.timetables || []);
      } catch (e) {
        setTimetable([]);
      } finally {
        setTtLoading(false);
      }
    };
    run();
  }, []);

  React.useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        setAnnouncementsLoading(true);
        const { communicationService } = await import('../../services/communicationService');
        const response = await communicationService.getAnnouncements({ 
          status: 'published',
          limit: 5 
        });
        const data = response.data?.data || response.data || [];
        setAnnouncements(Array.isArray(data) ? data : data.announcements || []);
      } catch (e) {
        console.error('Error fetching announcements:', e);
        setAnnouncements([]);
      } finally {
        setAnnouncementsLoading(false);
      }
    };
    fetchAnnouncements();
  }, []);

  const activeSubjects = new Set((timetable || []).map(p => p.subjectName)).size;
  const completedAssignments = (assignments || []).filter(a => a.status === 'completed' || a.status === 'submitted').length;
  const upcomingExams = exams || [];
  const averageGrade = Math.round(((grades || []).reduce((s, g) => s + (g.percentage || g.score || 0), 0) / Math.max((grades || []).length, 1)) || 0);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'in-progress': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (assignmentsLoading || examsLoading || gradesLoading || ttLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="w-full">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
            Student Dashboard
          </h1>
          <p className="text-gray-600">
            Welcome back, {user?.firstName || 'Student'}! Here's your academic overview.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center">
                <BookOpen className="h-6 w-6 sm:h-8 sm:w-8 text-blue-600" />
                <div className="ml-3 sm:ml-4">
                  <p className="text-xs sm:text-sm font-medium text-gray-600">Active Subjects</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">{activeSubjects}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center">
                <CheckCircle className="h-6 w-6 sm:h-8 sm:w-8 text-green-600" />
                <div className="ml-3 sm:ml-4">
                  <p className="text-xs sm:text-sm font-medium text-gray-600">Completed Assignments</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">{completedAssignments}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center">
                <Calendar className="h-6 w-6 sm:h-8 sm:w-8 text-purple-600" />
                <div className="ml-3 sm:ml-4">
                  <p className="text-xs sm:text-sm font-medium text-gray-600">Upcoming Exams</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">{upcomingExams.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-center">
                <Award className="h-6 w-6 sm:h-8 sm:w-8 text-yellow-600" />
                <div className="ml-3 sm:ml-4">
                  <p className="text-xs sm:text-sm font-medium text-gray-600">Average Grade</p>
                  <p className="text-xl sm:text-2xl font-bold text-gray-900">{isFinite(averageGrade) ? averageGrade : 0}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          {/* Recent Assignments */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-base sm:text-lg">
                <FileText className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                Recent Assignments
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 sm:space-y-4">
                {(assignments || []).slice(0,4).map((assignment) => (
                  <div key={assignment.id} className="flex items-center justify-between p-3 sm:p-4 border rounded-lg">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm sm:text-base font-medium text-gray-900 truncate">
                        {assignment.title}
                      </h4>
                      <p className="text-xs sm:text-sm text-gray-600">
                        {assignment.subjectName || assignment.subject} • Due: {assignment.dueDate}
                      </p>
                      {assignment.grade && (
                        <p className="text-xs text-green-600 font-medium">Grade: {assignment.grade}</p>
                      )}
                    </div>
                    <div className="text-right ml-2">
                      <Badge className={`text-xs ${getStatusColor(assignment.status)}`}>
                        {assignment.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Upcoming Exams */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-base sm:text-lg">
                <Calendar className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                Upcoming Exams
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 sm:space-y-4">
                {upcomingExams.map((exam) => (
                  <div key={exam.id} className="flex items-center justify-between p-3 sm:p-4 border rounded-lg">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm sm:text-base font-medium text-gray-900">
                        {exam.subjectName || exam.subject}
                      </h4>
                      <p className="text-xs sm:text-sm text-gray-600">{exam.examDate} at {exam.startTime}</p>
                      <p className="text-xs text-gray-500">{exam.room || ''}</p>
                    </div>
                    <div className="text-right ml-2">
                      <Badge className="bg-blue-100 text-blue-800 text-xs">
                        {exam.duration} mins
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Today's Timetable */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-base sm:text-lg">
                <Clock className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                Today's Timetable
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 sm:space-y-4">
                {(timetable || []).map((period, index) => (
                  <div key={index} className="flex items-center justify-between p-3 sm:p-4 border rounded-lg">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm sm:text-base font-medium text-gray-900">
                        {period.subjectName || period.subject}
                      </h4>
                      {period.teacherName && (
                        <p className="text-xs sm:text-sm text-gray-600">{period.teacherName}</p>
                      )}
                      <p className="text-xs text-gray-500">{period.room || ''}</p>
                    </div>
                    <div className="text-right ml-2">
                      <p className="text-xs sm:text-sm text-gray-600">{period.time}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Recent Grades */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-base sm:text-lg">
                <Award className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                Recent Grades
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 sm:space-y-4">
                {(grades || []).slice(0,4).map((grade, index) => (
                  <div key={index} className="flex items-center justify-between p-3 sm:p-4 border rounded-lg">
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm sm:text-base font-medium text-gray-900">
                        {grade.assessmentTitle || grade.assignment}
                      </h4>
                      <p className="text-xs sm:text-sm text-gray-600">{grade.subjectName || grade.subject}</p>
                      <p className="text-xs text-gray-500">{grade.dateAwarded || ''}</p>
                    </div>
                    <div className="text-right ml-2">
                      <Badge className="bg-green-100 text-green-800 text-xs">
                        {grade.grade || Math.round(grade.percentage)}
                      </Badge>
                      {grade.percentage != null && (
                        <p className="text-xs text-gray-500 mt-1">{Math.round(grade.percentage)}%</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Announcements */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center text-base sm:text-lg">
                <AlertCircle className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                School Announcements
              </CardTitle>
            </CardHeader>
            <CardContent>
              {announcementsLoading ? (
                <div className="flex items-center justify-center py-4">
                  <Clock className="w-5 h-5 animate-spin text-gray-400" />
                </div>
              ) : announcements && announcements.length > 0 ? (
                <div className="space-y-3 sm:space-y-4">
                  {announcements.map((announcement) => (
                    <div key={announcement.id} className="flex items-center justify-between p-3 sm:p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm sm:text-base font-medium text-gray-900">
                          {announcement.title || announcement.subject}
                        </h4>
                        <p className="text-xs sm:text-sm text-gray-600 mt-1">
                          {new Date(announcement.createdAt || announcement.date).toLocaleDateString()}
                        </p>
                        {announcement.type && (
                          <p className="text-xs text-gray-500 capitalize mt-1">{announcement.type}</p>
                        )}
                      </div>
                      <Badge className={`text-xs ${getPriorityColor(announcement.priority || 'normal')}`}>
                        {announcement.priority || 'normal'}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 text-sm">
                  No announcements at the moment
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Actions */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base sm:text-lg">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-3 sm:gap-4">
                <button className="p-3 sm:p-4 border rounded-lg hover:bg-gray-50 text-center transition-colors">
                  <FileText className="h-5 w-5 sm:h-6 sm:w-6 mx-auto mb-2 text-blue-600" />
                  <p className="text-xs sm:text-sm font-medium">Submit Assignment</p>
                </button>
                <button className="p-3 sm:p-4 border rounded-lg hover:bg-gray-50 text-center transition-colors">
                  <BookOpen className="h-5 w-5 sm:h-6 sm:w-6 mx-auto mb-2 text-green-600" />
                  <p className="text-xs sm:text-sm font-medium">View Subjects</p>
                </button>
                <button className="p-3 sm:p-4 border rounded-lg hover:bg-gray-50 text-center transition-colors">
                  <Award className="h-5 w-5 sm:h-6 sm:w-6 mx-auto mb-2 text-purple-600" />
                  <p className="text-xs sm:text-sm font-medium">View Grades</p>
                </button>
                <button className="p-3 sm:p-4 border rounded-lg hover:bg-gray-50 text-center transition-colors">
                  <Calendar className="h-5 w-5 sm:h-6 sm:w-6 mx-auto mb-2 text-yellow-600" />
                  <p className="text-xs sm:text-sm font-medium">View Schedule</p>
                </button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
  );
};

export default StudentDashboard;